import React, { useState } from 'react'
import {
    Box,
    Button,
    Grid,
    LinearProgress,
    IconButton,
    Typography
} from "@mui/material"
import { styled } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { API_URL } from '../../../constants';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CustomShowModal from '../../Common/CustomShowModal';
import CustomAudioPlayer from '../../Common/CustomAudioPlayer';

const StyledButton = styled(Button)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '1rem',
    fontWeight: 600,
    height: 48,
    minWidth: 160,
    borderRadius: '12px',
    border: '2px solid',
    borderColor: "#1a73e8",
    color: "#1a73e8",
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'rgba(26, 115, 232, 0.08)',
        borderColor: "#1557b0",
        transform: 'translateY(-2px)',
    }
}));

const StyledSubmit = styled(Button)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #00bfa5 0%, #00897b 100%)',
    fontSize: '1rem',
    fontWeight: 600,
    height: 48,
    minWidth: 150,
    borderRadius: '12px',
    color: "white",
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(0, 191, 165, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
        boxShadow: '0 6px 20px rgba(0, 191, 165, 0.4)',
        transform: 'translateY(-2px)',
    }
}));

const StyledContained = styled(Button)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    fontSize: '1rem',
    fontWeight: 600,
    height: 48,
    minWidth: 200,
    borderRadius: '12px',
    color: "white",
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
        boxShadow: '0 6px 20px rgba(26, 115, 232, 0.4)',
        transform: 'translateY(-2px)',
    }
}));

const NextButton = styled(Button)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #00bfa5 0%, #00897b 100%)',
    fontSize: '1rem',
    fontWeight: 600,
    height: 48,
    minWidth: 150,
    borderRadius: '12px',
    color: "white",
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(0, 191, 165, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
        boxShadow: '0 6px 20px rgba(0, 191, 165, 0.4)',
        transform: 'translateY(-2px)',
    }
}));

const UploadCard = styled(Box)({
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '2px solid rgba(26, 115, 232, 0.2)',
    marginBottom: '20px',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        borderColor: 'rgba(26, 115, 232, 0.4)',
    },
});

const FileUploadLabel = styled('label')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '120px',
    borderRadius: '12px',
    border: '2px dashed rgba(26, 115, 232, 0.3)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: 'rgba(26, 115, 232, 0.6)',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e8f4f8 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(26, 115, 232, 0.15)',
    }
});

const FileUploadContent = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '20px',
});

const FileUploadButton = styled(Box)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
    padding: '10px 24px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.95rem',
    boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)',
});

const FileUploadText = styled(Box)({
    fontFamily: "'DM Sans', sans-serif",
    color: '#666',
    fontSize: '0.875rem',
});

function SongInfo(props) {
    const {
        isSingleTrack = false,
        onSongFileHandler,
        showUpload,
        isProgressDone,
        uploadProgress,
        setCurrentEditIndex,
        savedSongsDetails = [],
        addNewTrack,
        deleteSavedSongDetails,
        setOpenSongDetail,
        onSaveSongDetails,
        songInfoMusicLink,
        selectedAudioFile,
        onClickSongEdit,
        showSubmitSong,
        songInfo,
        disableTab = {},
        handleTabChange
    } = props;

    const [open, setOpen] = useState(false)
    const [localAudioUrl, setLocalAudioUrl] = useState(null)

    // Create blob URL for local file
    React.useEffect(() => {
        if (selectedAudioFile) {
            const url = URL.createObjectURL(selectedAudioFile);
            setLocalAudioUrl(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setLocalAudioUrl(null);
        }
    }, [selectedAudioFile]);

    return (
        <Grid container className="songinfo-container">
            <Grid item xs={12}>
                <h3>Upload Assets</h3>
            </Grid>

            <Grid item xs={12}>
                <a className="artwork-guidlines" onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>AUDIO FILE GUIDELINES</a>
            </Grid>
            <Grid item xs={12}>

                {savedSongsDetails && savedSongsDetails.length ? savedSongsDetails.map((item, index) => {
                    if (item.saved) {
                        return <Grid container className="song-detail-saved" key={index}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {/* Title - 30% */}
                                    <Box sx={{ width: '30%' }}>
                                        <Typography sx={{ fontWeight: 600, color: '#202124', fontSize: '0.95rem', fontFamily: "'DM Sans', sans-serif" }}>
                                            <span style={{ fontWeight: 500, color: '#5f6368' }}>Title:</span> {item.title}
                                        </Typography>
                                    </Box>

                                    {/* Player - 55% */}
                                    {item.audio_track && (
                                        <Box sx={{ width: '55%' }}>
                                            <CustomAudioPlayer src={`${API_URL}/${item.audio_track}`} />
                                        </Box>
                                    )}

                                    {/* Edit Button - remaining space */}
                                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                        <StyledButton variant="outlined" onClick={() => onClickSongEdit(index)}>Edit</StyledButton>
                                    </Box>
                                </Box>

                            </Grid>
                        </Grid>
                    } else {
                        return <Grid container className="song-upload-details" key={index}>
                            <Grid item xs={12} sm={12} md={6}>
                                <Box>
                                    {showUpload ?
                                        <Box sx={{
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            border: '2px solid rgba(26, 115, 232, 0.2)',
                                        }}>
                                            {isProgressDone ?
                                                <Box>
                                                    {songInfoMusicLink ? (
                                                        <CustomAudioPlayer src={`${API_URL}/${songInfoMusicLink}`} />
                                                    ) : selectedAudioFile && localAudioUrl ? (
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1 }}>
                                                                <MusicNoteIcon sx={{ color: '#1a73e8', fontSize: '20px' }} />
                                                                <Typography sx={{ fontWeight: 600, color: '#202124', fontSize: '0.875rem', fontFamily: "'DM Sans', sans-serif" }}>
                                                                    {selectedAudioFile.name}
                                                                </Typography>
                                                                <Typography sx={{ color: '#5f6368', fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif" }}>
                                                                    ({(selectedAudioFile.size / (1024 * 1024)).toFixed(2)} MB)
                                                                </Typography>
                                                            </Box>
                                                            <CustomAudioPlayer src={localAudioUrl} />
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                                :
                                                <Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={uploadProgress}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            backgroundColor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': {
                                                                background: 'linear-gradient(90deg, #1a73e8 0%, #00bfa5 100%)',
                                                                borderRadius: 4,
                                                            }
                                                        }}
                                                    />
                                                    <Box sx={{ textAlign: 'center', marginTop: '10px', fontWeight: 600, color: '#1a73e8' }}>
                                                        {uploadProgress}%
                                                    </Box>
                                                </Box>
                                            }
                                        </Box>
                                        :
                                        <>
                                            <FileUploadLabel htmlFor="song-upload-file">
                                                <FileUploadContent>
                                                    <FileUploadButton>Choose File</FileUploadButton>
                                                    <FileUploadText>Click here to select an audio file...</FileUploadText>
                                                </FileUploadContent>
                                            </FileUploadLabel>
                                            <input
                                                type="file"
                                                id="song-upload-file"
                                                accept=".mp3,audio/*"
                                                onChange={(e) => onSongFileHandler(e, index)}
                                                style={{ display: 'none' }}
                                            />
                                        </>
                                    }
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <Box
                                    display="flex"
                                    height="100%"
                                    alignItems="center"
                                    gap={2}
                                    sx={{
                                        padding: { xs: '20px 0', md: '0 20px' },
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <StyledContained
                                        variant="contained"
                                        onClick={() => { setCurrentEditIndex(index); setOpenSongDetail(true) }}
                                    >
                                        {songInfo.edit ? <UpdateIcon style={{ fontSize: "18px", marginRight: "5px" }} /> : <AddCircleIcon style={{ fontSize: "18px", marginRight: "5px" }} />}
                                        {songInfo.edit ? 'Edit' : 'Add'} Song Details
                                    </StyledContained>
                                    {showSubmitSong ? <StyledSubmit onClick={onSaveSongDetails}>{songInfo.edit ? 'Save' : 'Submit'}</StyledSubmit> : null}
                                    {(!showSubmitSong && !songInfo.edit && index !== 0) ?
                                        <IconButton
                                            onClick={() => deleteSavedSongDetails(index)}
                                            sx={{
                                                color: '#f44336',
                                                '&:hover': {
                                                    background: 'rgba(244, 67, 54, 0.1)',
                                                }
                                            }}
                                        >
                                            <DeleteForeverIcon />
                                        </IconButton>
                                    : null}
                                </Box>
                            </Grid>
                        </Grid>
                    }
                }) : null}

            </Grid>

            {isSingleTrack ? null : <Grid item xs={12}>
                <div style={{ width: "100%", paddingRight: 10 }}>
                    <StyledButton variant="outlined" style={{ float: "right" }} onClick={addNewTrack}>
                        <AddIcon style={{ fontSize: "18px", marginRight: "5px" }} />
                        Add Track
                    </StyledButton>
                </div>
            </Grid>}

            {/* Next Button - Show when at least one audio is submitted and Platform is unlocked */}
            {savedSongsDetails.some(item => item.saved) && !disableTab.third && handleTabChange && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pr: 1.25 }}>
                        <NextButton
                            variant="contained"
                            onClick={() => handleTabChange({}, 3)}
                            endIcon={<ArrowForwardIcon />}
                        >
                            Next: Platforms
                        </NextButton>
                    </Box>
                </Grid>
            )}

            <CustomShowModal
                open={open}
                handleClose={() => setOpen(false)}
                maxWidth="sm"
                title="AUDIO FILE GUIDELINES"
            >
                <p>The audio files should not have any sound imperfection. Audio files containing any background noise and other sound imperfections will not be accepted for distribution.</p>

                <p style={{ marginTop: 20 }}>Format: mp3 or wav</p>
                <p style={{ marginTop: 10 }}><span style={{ fontWeight: 600 }}>Requirements:</span> Minimum of 16 bit, 44.1 Khz, stereo</p>
                <p><span style={{ fontWeight: 600 }}>Recommended</span> 24 bits, 48Khz or 24 bits 96Khz.</p>
            </CustomShowModal>
        </Grid >
    )
}

export default SongInfo
